import { useCallback, useMemo } from "react"

import ColumnChooserDialog from "~/components/planning/columnChooser/ColumnChooserDialog"
import { useShownColumnIds } from "~/components/planning/ProductTable/useProductTableColumns"
import { type GridOwnedChooserCapability } from "~/components/planning/TableRegistryContext"
import {
  commitShownColumns,
  expandGroups,
  NO_GROUPS,
  resetAbsentChoices,
  shownChooserIds,
} from "~/utils/gridOwnedColumns"

type Props = GridOwnedChooserCapability & { tableId: string }

/**
 * The column chooser of a `useGridOwnedColumns` table: ticks the entries its grid shows, a group's
 * while any member is, and commits toggles to it without re-rendering the table.
 */
export default function GridOwnedColumnChooser({
  columnIds,
  defaultColumnIds,
  fallbackColumnTitles,
  columnGroups = NO_GROUPS,
  chooser,
  tableId,
}: Props) {
  const memberIds = useMemo(() => expandGroups(columnIds, columnGroups), [columnIds, columnGroups])
  const defaultMemberIds = useMemo(
    () => expandGroups(defaultColumnIds, columnGroups),
    [defaultColumnIds, columnGroups],
  )
  const shown = useShownColumnIds(tableId, memberIds, defaultMemberIds)
  const displayedColumnIds = useMemo(
    () => shownChooserIds(columnIds, columnGroups, (colId) => shown.has(colId)),
    [columnIds, columnGroups, shown],
  )
  const resetToDefault = useCallback(
    () => resetAbsentChoices(tableId, defaultColumnIds, columnGroups),
    [tableId, defaultColumnIds, columnGroups],
  )

  return (
    <ColumnChooserDialog
      {...chooser}
      columnIds={columnIds}
      displayedColumnIds={displayedColumnIds}
      setDisplayedColumnIds={(ids) => commitShownColumns(tableId, columnIds, ids)}
      fallbackColumnTitles={fallbackColumnTitles}
      defaultColumnIds={defaultColumnIds}
      onResetToDefaultApplied={resetToDefault}
      tableId={tableId}
    />
  )
}
